/**
 * App bootstrap.
 *   1. Global CSS.
 *   2. Configure api-client base URL.
 *   3. Install the realm session's live token as the api-client's token
 *      provider, before anything renders, so no request leaves without it.
 *   4. Mount <App />. (The <OidcTokenBridge /> inside <App /> keeps the
 *      session renewed.)
 *
 * Also defensively unregisters any lingering MSW service worker from an
 * earlier branch of this project — MSW has been removed, but an SW can
 * persist in the browser cache across reloads.
 */
import './styles/index.css';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { configureApiClient, setTokenProvider } from '@optum/api-client/client';

import { App } from './app.js';
import { liveToken } from './auth/session.js';

configureApiClient({ baseUrl: window.location.origin });
setTokenProvider(liveToken);

async function unregisterLegacyMockWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  const regs = await navigator.serviceWorker.getRegistrations();
  for (const reg of regs) {
    if (reg.active?.scriptURL.includes('mockServiceWorker.js')) {
      await reg.unregister();
    }
  }
}

await unregisterLegacyMockWorker();

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Missing #root element in index.html');

createRoot(rootEl).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
